import Loader from "atoms/loaders/loader";
import CurrentPublicationEntityRecord from "models/view-models/atoms/current-publication-entity-record";
import UnorderedList from "molecules/lists/unordered-list";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import CurrentPublicationEntityAtom from "utilities/atoms/current-publication-entity-atom";
import { CollectionUtils } from "utilities/collection-utils";
import useUpdateAtomEffect from "utilities/hooks/atoms/use-update-atom-value-effect";
import useParts from "utilities/hooks/domain/publications/chapters/articles/parts/use-parts";
import useSections from "utilities/hooks/domain/publications/chapters/articles/sections/use-sections";
import useArticle from "utilities/hooks/domain/publications/chapters/articles/use-article";
import useChapter from "utilities/hooks/domain/publications/chapters/use-chapter";
import { useErrorBoundary } from "utilities/hooks/use-error-boundary";
import useErrors from "utilities/hooks/use-errors";
import useLoading from "utilities/hooks/use-loading";
import { useLocalization } from "utilities/hooks/use-localization";
import useCurrentPublication from "utilities/contexts/use-current-publication";
import PublicationComponentType from "utilities/enumerations/publication-component-type";
import useVirtualTableOfContents from "pages/notebooks/notebook-reference-panel/bookview/use-virtual-table-of-contents";
import CaapsItemTitleAreaWithinPanel from "organisms/publication-content/notebook-reference-panel/caaps-title-area-within-panel";
import PublicationContentAreaWithinPanel from "organisms/publication-content/notebook-reference-panel/publication-content-area-within-panel";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface ArticleContentWithinPanelProps {}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS_NAME = "c-publication-page-layout";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ArticleContentWithinPanel: React.FunctionComponent<
    ArticleContentWithinPanelProps
> = (props: ArticleContentWithinPanelProps) => {
    const { publication } = useCurrentPublication();
    const { currentTocItem } = useVirtualTableOfContents();
    const { code, edition, chapterNfpaLabel, nfpaLabel, chapterId } = {
        code: publication?.code,
        edition: publication?.edition,
        chapterNfpaLabel: currentTocItem?.nfpaLabel,
        nfpaLabel: currentTocItem?.nfpaLabel,
        chapterId: Number(currentTocItem?.parentId),
    };

    const { t } = useLocalization();
    const [resetFocus, setResetFocus] = useState<boolean>(false);

    const loaderText = t("loadingItem", {
        item: t("article"),
    });
    const articleContentRef = useRef<HTMLDivElement>(null);

    // -----------------------------------------------------------------------------------------
    // #region Service Hooks
    // -----------------------------------------------------------------------------------------

    const {
        resultObject: chapter,
        loading: chapterLoading,
        errors: chapterErrors,
    } = useChapter({
        publicationId: publication?.id,
        chapterId: chapterId,
        nfpaLabel: chapterNfpaLabel ?? "",
    });

    const {
        resultObject: article,
        loading: articleLoading,
        loaded: articleLoaded,
        errors: articleErrors,
    } = useArticle({
        chapterId: chapter.id,
        code,
        edition,
        nfpaLabel,
        articleId: "",
        publicationId: publication?.id,
    });
    const {
        resultObject: parts,
        loading: partsLoading,
        errors: partsErrors,
    } = useParts({
        articleId: article.id,
        chapterId: chapter.id,
        pending: chapterLoading || articleLoading,
        publicationId: publication?.id,
    });
    const {
        resultObject: sections,
        loading: sectionLoading,
        errors: sectionsErrors,
    } = useSections({
        articleId: article.id,
        chapterId: chapter.id,
        pending: chapterLoading || articleLoading,
        publicationId: publication?.id,
    });
    const loading = useLoading(
        articleLoading,
        chapterLoading,
        partsLoading,
        sectionLoading
    );

    // #endregion Service Hooks

    // -----------------------------------------------------------------------------------------
    // #region Side Effects
    // -----------------------------------------------------------------------------------------

    const pageErrors = useErrors(
        chapterErrors,
        articleErrors,
        sectionsErrors,
        partsErrors
    );

    useErrorBoundary(pageErrors);

    useEffect(
        function setFocus() {
            if (resetFocus && articleContentRef && articleContentRef.current) {
                setResetFocus(false);
                articleContentRef.current.focus({ preventScroll: true });
            }
        },
        [resetFocus, article, articleLoading]
    );

    useUpdateAtomEffect({
        atom: CurrentPublicationEntityAtom,
        enabled: article != null && articleLoaded,
        value: new CurrentPublicationEntityRecord({
            entityId: article.id!,
            entityTitle: article.getDisplayTitle(),
            selected: true,
            type: "Article",
        }),
    });
    // #endregion Side Effects

    // -----------------------------------------------------------------------------------------
    // #region Render
    // -----------------------------------------------------------------------------------------
    const titleArea = useMemo(
        () => (
            <CaapsItemTitleAreaWithinPanel
                publication={publication}
                externalId={article.externalId}
                titleClassName={""}
                record={article}
                publicationComponentType={PublicationComponentType.Article}
            />
        ),
        [article, publication]
    );

    return (
        <Fragment>
            {CollectionUtils.hasValues(pageErrors) && (
                <UnorderedList
                    cssClassName={`${BASE_CLASS_NAME}__errors`}
                    listItems={pageErrors}
                />
            )}
            {loading ? (
                <Loader accessibleText={loaderText} />
            ) : (
                <>
                    <PublicationContentAreaWithinPanel
                        titleArea={titleArea}
                        parts={parts}
                        sections={sections.map((s) => s.with({ publication }))}
                        externalId={article.externalId}
                    />
                </>
            )}
        </Fragment>
    );
    // #endregion Render
};
// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default ArticleContentWithinPanel;

// #endregion Exports
